import React, { useCallback, useEffect } from 'react';
import { Transportation, VehicleRec, ascCtaInteractionInput, ascPageviewInput } from 'src/models';
import { Dialog } from '@framework-controls/core';
import { strings } from 'src/localization';
import css from './SpecialConsiderationMessage.module.scss';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import { IconRemove, IconRemoveAudi } from 'src/content/icons';
import { THEME } from 'src/constants/ThemeNames';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

interface IProps {
    isOpen: boolean;
    selectedTime: string;
    transportation: Transportation;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme?: string;
    proceedHandler: (time: string, transportation: Transportation) => void;
    cancelHandler: () => void;
}

const SpecialConsiderationMessage = (props: IProps) => {
    // Alpha's close button is a bare X (IconRemoveAudi); non-Audi themes keep the original close markup.
    const isAudi = props.theme === THEME.AUDI;
    const { title, information } = props.transportation;

    const proceedHandler = useCallback((e) => {
        props.proceedHandler(props.selectedTime, props.transportation);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.OkLit,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.transportation, props.selectedTime, props.proceedHandler, props.vehicles, props.selectedVehicle]);

    const cancelHandler = useCallback((e: any, text: string) => {
        props.cancelHandler();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: text,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.cancelHandler, props.vehicles, props.selectedVehicle]);

    // Audi only: Enter/Space on the close wrapper runs the same close as onClick.
    const closeKeyHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            cancelHandler(e, '');
        }
    }, [cancelHandler]);

    useEffect(() => {
        if (props.isOpen) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.isOpen, props.vehicles, props.selectedVehicle]);

    return (
        <Dialog
            isOpen={props.isOpen}
            onCancel={(e) => cancelHandler(e, '')}
            onBackDropClick={props.cancelHandler}
            header={title}
            hideCancel
            hideSave
            cssClasses={{
                content: classNames(css.content, { [css.contentAudi]: isAudi }),
                paper: classNames(css.paper, { [css.paperAudi]: isAudi }),
                header: css.LightBoxHeader,
            }}
        >
            <div className={classNames(css.headerbox, { [css.headerboxAudi]: isAudi })}>
                {title}
                {isAudi ?
                    <div className={classNames(css.headerIcon, css.headerIconAudi)}
                        onClick={(e) => cancelHandler(e, '')}
                        onKeyDown={closeKeyHandler}
                        role='button'
                        tabIndex={0}
                        aria-label={strings.Close}>
                        <IconRemoveAudi id='closeBtn'/>
                    </div> :
                    <div className={css.headerIcon}>
                        <IconRemove id='closeBtn' onClick={(e) => cancelHandler(e, '')}/>
                    </div>}
            </div>
            <div className={css.specArea}>
                <div style={{ marginTop: '1rem', marginBottom: '1rem', whiteSpace: 'break-spaces' }}>
                    {information}
                </div>
                <div className={css.footer}>
                    <hr className={css.horizLine} />
                    <div className={css.btnArea}>
                        <SVPButton
                            id='secondary'
                            handler={(e) => cancelHandler(e, strings.Cancel)}
                            message={strings.Cancel}
                            noArrow={true}
                            variant={'secondaryBtnAlt'}
                            theme={props.theme}
                        />
                        <SVPButton
                            id='primary'
                            handler={proceedHandler}
                            message={strings.OkLit}
                            variant={'primaryBtnAlt'}
                            noArrow={true}
                            theme={props.theme}/>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default SpecialConsiderationMessage;